import React, { useRef, useState } from "react";

import { Button } from "@/client/components/ui";
import { useKey } from "@/client/hooks/useKey";
import { CheckIcon, CopyIcon } from "@/client/icons";

export function CookbookModal({
  cookbooks,
  onClose,
}: {
  cookbooks: string[];
  onClose: () => void;
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [names, setNames] = useState<string[]>(cookbooks.map((_, i) => `Persona ${i + 1}`));
  const [copied, setCopied] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useKey("Escape", onClose);

  function getPersonalizedMarkdown(idx: number): string {
    return cookbooks[idx]!.replace(/# Cookbook - Persona \d+/, `# Cookbook - ${names[idx]}`);
  }

  async function copyToClipboard() {
    await navigator.clipboard.writeText(getPersonalizedMarkdown(activeTab));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handlePrint() {
    const content = getPersonalizedMarkdown(activeTab);
    const html = markdownToHtml(content);
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>Cookbook - ${names[activeTab]}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Nunito+Sans:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  body { font-family: "Nunito Sans", "Segoe UI", sans-serif; max-width: 700px; margin: 0 auto; padding: 2rem; color: #2e2520; font-size: 13px; }
  h1 { font-family: "DM Serif Display", Georgia, serif; font-size: 1.5rem; margin-bottom: 0.25rem; }
  h2 { font-family: "DM Serif Display", Georgia, serif; font-size: 1.1rem; margin-top: 1.5rem; margin-bottom: 0.5rem; }
  hr { border: none; border-top: 1px solid #ddd; margin: 1rem 0; }
  ul { padding-left: 1.2rem; }
  li { margin-bottom: 0.25rem; }
  p { margin: 0 0 3px 0; font-size: 13px; color: #7a6b5d; }
  p strong { color: #2e2520; }
  p em { color: #b5a394; font-size: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { text-align: left; padding: 4px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
  th { font-weight: 600; border-bottom: 2px solid #ccc; }
  td:first-child { white-space: nowrap; width: 60px; }
  @media print { body { padding: 0; } }
</style>
</head><body>${html}</body></html>`);
    win.document.close();
    setTimeout(() => {
      win.print();
    }, 300);
  }

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 animate-[fade-in_0.15s_ease]"
        onClick={onClose}
      />
      <div className="relative bg-term-surface border border-term-border rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-term-border">
          <h2 className="font-serif text-lg text-term-text">Cookbooks</h2>
          <button
            onClick={onClose}
            className="text-term-text-muted hover:text-term-text cursor-pointer w-7 h-7 flex items-center justify-center rounded-full hover:bg-term-raised transition-all text-lg leading-none"
          >
            ×
          </button>
        </div>

        {/* Tabs + name input */}
        <div className="flex items-center gap-3 px-5 pt-4 pb-2">
          <div className="flex items-center gap-1">
            {cookbooks.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  activeTab === i
                    ? "bg-term-accent text-white"
                    : "text-term-text-muted hover:text-term-text hover:bg-term-raised cursor-pointer"
                }`}
              >
                {names[i]}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <input
            value={names[activeTab]}
            onChange={(e) =>
              setNames((prev) => prev.map((n, i) => (i === activeTab ? e.target.value : n)))
            }
            className="text-sm bg-term-bg border border-term-border rounded-lg px-2 py-1 text-term-text w-32 focus:border-term-accent-dim focus:outline-none"
            placeholder="Nombre..."
          />
        </div>

        {/* Content */}
        <div ref={printRef} className="flex-1 overflow-y-auto px-5 pb-5">
          <div
            className="cookbook-content max-w-none text-term-text selectable
              [&_h1]:font-serif [&_h1]:text-xl [&_h1]:text-term-text [&_h1]:mb-1
              [&_h2]:font-serif [&_h2]:text-base [&_h2]:text-term-text [&_h2]:mt-5 [&_h2]:mb-2
              [&_hr]:border-term-border [&_hr]:my-3
              [&_ul]:pl-4 [&_ul]:text-sm [&_li]:text-term-text-dim [&_li]:mb-0.5
              [&_table]:w-full [&_table]:text-sm
              [&_th]:text-left [&_th]:py-1.5 [&_th]:px-2 [&_th]:border-b-2 [&_th]:border-term-border [&_th]:text-term-text-muted [&_th]:font-medium [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-micro
              [&_td]:py-1.5 [&_td]:px-2 [&_td]:border-b [&_td]:border-term-border/50 [&_td]:align-top
              [&_td:first-child]:whitespace-nowrap [&_td:first-child]:w-14 [&_td:first-child]:text-term-text-muted
              [&_strong]:text-term-text [&_strong]:font-semibold
              [&_p]:text-sm [&_p]:text-term-text-dim [&_p]:mb-1
              [&_p>em:only-child]:text-term-text-muted [&_p>em:only-child]:text-sm"
            dangerouslySetInnerHTML={{ __html: markdownToHtml(getPersonalizedMarkdown(activeTab)) }}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-term-border">
          <Button variant="secondary" size="sm" onClick={copyToClipboard}>
            {copied ? <CheckIcon width={13} height={13} /> : <CopyIcon width={13} height={13} />}
            {copied ? "Copiado" : "Copiar markdown"}
          </Button>
          <Button variant="primary" size="sm" onClick={handlePrint}>
            Imprimir
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Minimal markdown → HTML converter for cookbook content */
function markdownToHtml(md: string): string {
  const lines = md.split("\n");
  const out: string[] = [];
  let inTable = false;
  let inList = false;

  for (const line of lines) {
    // Table
    if (line.startsWith("|")) {
      if (line.match(/^\|[\s-|]+\|$/)) continue; // separator row
      const cells = line
        .split("|")
        .slice(1, -1)
        .map((c) => c.trim());
      if (!inTable) {
        inTable = true;
        out.push(
          "<table><thead><tr>" +
            cells.map((c) => `<th>${inlineMd(c)}</th>`).join("") +
            "</tr></thead><tbody>",
        );
      } else {
        out.push("<tr>" + cells.map((c) => `<td>${inlineMd(c)}</td>`).join("") + "</tr>");
      }
      continue;
    }
    if (inTable) {
      inTable = false;
      out.push("</tbody></table>");
    }

    // List
    if (line.startsWith("- ")) {
      if (!inList) {
        inList = true;
        out.push("<ul>");
      }
      out.push(`<li>${inlineMd(line.slice(2))}</li>`);
      continue;
    }
    if (inList) {
      inList = false;
      out.push("</ul>");
    }

    // Headings
    if (line.startsWith("## ")) {
      out.push(`<h2>${inlineMd(line.slice(3))}</h2>`);
      continue;
    }
    if (line.startsWith("# ")) {
      out.push(`<h1>${inlineMd(line.slice(2))}</h1>`);
      continue;
    }

    // HR
    if (line === "---") {
      out.push("<hr>");
      continue;
    }

    // Empty line → visual spacer
    if (line.trim() === "") {
      out.push('<div style="height:8px"></div>');
      continue;
    }

    // Paragraph
    out.push(`<p>${inlineMd(line)}</p>`);
  }

  if (inTable) out.push("</tbody></table>");
  if (inList) out.push("</ul>");

  return out.join("\n");
}

function inlineMd(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code>$1</code>");
}
